import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Accordion, Dropdown, Row, Col } from 'react-bootstrap';
import apiClient from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from 'react-toastify';
import ConfirmationModal from '../components/ConfirmationModal';
import ArrangementModal from '../components/ArrangementModal';
import UploadFileModal from '../components/UploadFileModal';
import ChordProRenderOptionsModal from '../components/TransposeModal';
import { SecureAudioPlayer } from '../components/SecureFileComponents';
import { getSecureFileUrl } from '../utils/fileUtils';

// Custom toggle for the actions dropdown
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="btn btn-link text-dark p-2 border-0"
    >
      {children}
    </button>
));

const SongDetailPage = () => {
    const { songId } = useParams();
    const navigate = useNavigate();
    const { hash } = useLocation();
    const { user: currentUser, organization } = useAuth();
    const { canUpdate, canDelete, canCreate, loading: permissionsLoading } = usePermissions('song');
    
    const [song, setSong] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isCopying, setIsCopying] = useState(false);

    // Состояния для модальных окон
    const [showArrangementModal, setShowArrangementModal] = useState(false);
    const [isNewArrangement, setIsNewArrangement] = useState(true);
    const [currentArrangement, setCurrentArrangement] = useState(null);
    const [showUploadFileModal, setShowUploadFileModal] = useState(false);
    const [arrangementForUpload, setArrangementForUpload] = useState(null);
    const [showConfirmDeleteArrangementModal, setShowConfirmDeleteArrangementModal] = useState(false);
    const [arrangementToDeleteId, setArrangementToDeleteId] = useState(null);
    const [showConfirmDeleteSongModal, setShowConfirmDeleteSongModal] = useState(false);
    const [showConfirmDeleteFileModal, setShowConfirmDeleteFileModal] = useState(false);
    const [fileToDeleteId, setFileToDeleteId] = useState(null);
    const [showLimitInfoModal, setShowLimitInfoModal] = useState(false);
    const [activeArrangementKey, setActiveArrangementKey] = useState(null);
    const [showRenderOptionsModal, setShowRenderOptionsModal] = useState(false);
    const [fileForRender, setFileForRender] = useState(null);

    const isOwnSong = useMemo(() => currentUser && song && song.organization_id === currentUser.organization_id, [currentUser, song]);

    const formatSizeMB = (bytes) => {
        if (!bytes) return '';
        const mb = bytes / (1024 * 1024);
        return `(${(mb).toFixed(2)} MB)`;
    };

    const fetchSong = useCallback(() => {
        setLoading(true);
        apiClient.get(`/songs/${songId}`)
            .then(response => {
                setSong(response.data);
                setTitle(response.data.title);
            })
            .catch(err => {
                setError('Ошибка загрузки песни');
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [songId]);

    useEffect(() => {
        fetchSong();
    }, [fetchSong]);

    const fileStats = useMemo(() => {
        if (!song || !song.arrangements) {
            return { count: 0, totalSize: 0 };
        }

        let count = 0;
        let totalSize = 0;

        song.arrangements.forEach(arrangement => {
            if (arrangement.files) {
                count += arrangement.files.length;
                arrangement.files.forEach(file => {
                    totalSize += file.file_size || 0;
                });
            }
        });

        return { count, totalSize };
    }, [song]);

    const totalSizeMB = (fileStats.totalSize / (1024 * 1024)).toFixed(2);

    useEffect(() => {
        if (song && song.arrangements.length > 0) {
            const arrangementIdMatch = hash.match(/^#arrangement-(\d+)$/);
            if (arrangementIdMatch) {
                const arrangementId = arrangementIdMatch[1];
                if (song.arrangements.some(a => a.id.toString() === arrangementId)) {
                    setActiveArrangementKey(arrangementId);
                    return;
                }
            }

            const defaultArr = song.arrangements.find(a => a.is_default);
            if (defaultArr) {
                setActiveArrangementKey(defaultArr.id.toString());
            } else {
                const sortedArrangements = song.arrangements.slice().sort((a, b) => {
                    if (a.is_default && !b.is_default) return -1;
                    if (!a.is_default && b.is_default) return 1;
                    return a.name.localeCompare(b.name);
                });
                setActiveArrangementKey(sortedArrangements[0].id.toString());
            }
        }
    }, [song, hash]);

    const handleDeleteFile = (fileId) => {
        setFileToDeleteId(fileId);
        setShowConfirmDeleteFileModal(true);
    };

    const handleConfirmDeleteFile = async () => {
        try {
            await apiClient.delete(`/songs/files/${fileToDeleteId}`);
            toast.success('Файл успешно удален!');
            fetchSong();
        } catch (err) {
            toast.error('Ошибка удаления файла');
        } finally {
            setShowConfirmDeleteFileModal(false);
            setFileToDeleteId(null);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await apiClient.put(`/songs/${songId}`, { title });
            setIsEditing(false);
            fetchSong();
            toast.success('Песня успешно обновлена!');
        } catch (err) {
            toast.error('Ошибка обновления песни.');
        }
    };

    const handleDeleteSong = () => {
        setShowConfirmDeleteSongModal(true);
    };

    const handleConfirmDeleteSong = async () => {
        try {
            await apiClient.delete(`/songs/${songId}`);
            toast.success('Песня успешно удалена');
            navigate('/songs');
        } catch (err) {
            toast.error('Ошибка удаления песни.');
        } finally {
            setShowConfirmDeleteSongModal(false);
        }
    };

    const handleShowAddArrangementModal = () => {
        setIsNewArrangement(true);
        setCurrentArrangement(null);
        setShowArrangementModal(true);
    };

    const handleShowEditArrangementModal = (arrangement) => {
        setIsNewArrangement(false);
        setCurrentArrangement(arrangement);
        setShowArrangementModal(true);
    };

    const handleHideArrangementModal = () => setShowArrangementModal(false);

    const handleShowUploadFileModal = (arrangement) => {
        if (isOwnSong && organization && organization.storage_limit_bytes === 0) {
            setShowLimitInfoModal(true);
        } else {
            setArrangementForUpload(arrangement);
            setShowUploadFileModal(true);
        }
    };

    const handleHideUploadFileModal = () => {
        setArrangementForUpload(null);
        setShowUploadFileModal(false);
    };

    const handleSaveArrangement = async (arrangementData) => {
        try {
            if (isNewArrangement) {
                await apiClient.post(`/songs/${songId}/arrangements/`, arrangementData);
                toast.success('Аранжировка успешно добавлена!');
            } else {
                await apiClient.put(`/arrangements/${currentArrangement.id}/`, arrangementData);
                toast.success('Аранжировка успешно обновлена!');
            }
            fetchSong();
            handleHideArrangementModal();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Ошибка сохранения аранжировки');
        }
    };

    const handleDeleteArrangement = (arrangementId) => {
        setArrangementToDeleteId(arrangementId);
        setShowConfirmDeleteArrangementModal(true);
    };

    const handleConfirmDeleteArrangement = async () => {
        try {
            await apiClient.delete(`/arrangements/${arrangementToDeleteId}/`);
            fetchSong();
            toast.success('Аранжировка успешно удалена!');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Ошибка удаления аранжировки');
        } finally {
            setShowConfirmDeleteArrangementModal(false);
            setArrangementToDeleteId(null);
        }
    };

    const handleSetDefaultArrangement = async (arrangementId) => {
        try {
            await apiClient.post(`/songs/${songId}/arrangements/${arrangementId}/set_default/`);
            fetchSong(); // Re-fetch the full song data
            toast.success('Аранжировка по умолчанию изменена!');
        } catch (err) {
            toast.error('Ошибка установки аранжировки по умолчанию');
        }
    };

    const handleShowRenderOptionsModal = (file) => {
        setFileForRender(file);
        setShowRenderOptionsModal(true);
    };

    const handleRenderPdf = async (options) => {
        if (!fileForRender) return;
        
        setShowRenderOptionsModal(false);
        const newWindow = window.open('', '_blank');
        if (newWindow) newWindow.document.write('Загрузка PDF...');
        else {
            toast.error('Не удалось открыть новое окно. Возможно, оно было заблокировано вашим браузером.');
            return;
        }

        try {
            const params = new URLSearchParams();
            if (options.transpose !== 0) params.append('transpose', options.transpose);
            if (options.lyricsOnly) params.append('lyrics_only', 'true');
            if (options.noChordGrids) params.append('no_chord_grids', 'true');
            const queryString = params.toString();

            const response = await apiClient.get(`/songs/files/${fileForRender.id}/render?${queryString}`, {
                responseType: 'blob',
            });
            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            
            if (newWindow) {
                newWindow.location.href = fileURL;
            }
        } catch (error) {
            if (newWindow) newWindow.close();
            toast.error('Не удалось загрузить PDF для просмотра.');
            console.error('Error fetching PDF for preview:', error);
        } finally {
            setFileForRender(null);
        }
    };

    const handleViewExistingPdf = async (file) => {
        const newWindow = window.open('', '_blank');
        if (newWindow) newWindow.document.write('Загрузка PDF...');
        else {
            toast.error('Не удалось открыть новое окно. Возможно, оно было заблокировано вашим браузером.');
            return;
        }

        try {
            const fileURL = await getSecureFileUrl(file.file_path, 'inline');
            if (fileURL) {
                newWindow.location.href = fileURL;
            } else {
                throw new Error('Получен пустой URL для файла');
            }
        } catch (error) {
            if (newWindow) newWindow.close();
            toast.error('Не удалось загрузить PDF для просмотра.');
            console.error('Error fetching existing PDF for preview:', error);
        }
    };

    const handleDownloadFile = async (file) => {
        toast.info(`Загрузка файла ${file.original_filename}...`);
        try {
            const url = await getSecureFileUrl(file.file_path, 'attachment');
            if (url) {
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', file.original_filename || 'download');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                toast.error('Не удалось загрузить файл для скачивания.');
            }
        } catch (error) {
            console.error("Failed to download file", error);
            toast.error('Ошибка при скачивании файла.');
        }
    };

    const handleCopySong = async () => {
        if (!canCreate) {
            toast.warn('У вас нет прав на добавление песен.');
            return;
        }
        setIsCopying(true);
        try {
            const response = await apiClient.post(`/songs/${songId}/copy`);
            toast.success(`Песня "${song.title}" успешно скопирована.`);
            navigate(`/songs/${response.data.id}`);
        } catch (err) {
            if (err.response) {
                switch (err.response.status) {
                    case 409:
                        toast.error(`Ошибка: Песня с названием "${song.title}" уже существует в вашей организации.`);
                        break;
                    case 413:
                        toast.error('Ошибка: Недостаточно места в хранилище вашей организации.');
                        break;
                    default:
                        toast.error(`Произошла ошибка при копировании: ${err.response.data.detail || 'Неизвестная ошибка'}`);
                }
            } else {
                toast.error('Произошла ошибка сети при копировании песни.');
            }
        } finally {
            setIsCopying(false);
        }
    };

    if (loading || permissionsLoading) return <div className="text-center">Загрузка...</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;
    if (!song) return <div className="alert alert-warning">Песня не найдена.</div>;

    return (
        <>
            <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h1 className="card-title h3 mb-0">
                        {song.title}
                        {!isOwnSong && <span className="badge bg-secondary ms-2">{song.organization?.name}</span>}
                    </h1>
                    <div className="d-flex gap-2">
                        {canUpdate && isOwnSong && (
                            <button 
                                className="btn btn-sm btn-secondary"
                                onClick={() => setIsEditing(!isEditing)}
                                title={isEditing ? 'Отмена' : 'Редактировать'}
                            >
                                <i className={isEditing ? "fas fa-times" : "fas fa-pencil-alt"}></i>
                            </button>
                        )}
                        {canDelete && isOwnSong && (
                             <button 
                                className="btn btn-sm btn-danger" 
                                onClick={handleDeleteSong}
                                title="Удалить"
                            >
                                <i className="fas fa-trash"></i>
                            </button>
                        )}
                        {!isOwnSong && canCreate && (
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={handleCopySong}
                                disabled={isCopying}
                                title="Скопировать в свою фонотеку"
                            >
                                {isCopying ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    <><i className="fas fa-copy me-1"></i> Копировать</>
                                )}
                            </button>
                        )}
                    </div>
                </div>
                {(isEditing || isOwnSong) && (
                    <div className="card-body">
                        {isEditing && canUpdate && isOwnSong ? (
                            <form onSubmit={handleUpdate}>
                                <div className="mb-3">
                                    <label htmlFor="title" className="form-label">Название</label>
                                    <input type="text" id="title" className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
                                </div>
                                <button type="submit" className="btn btn-success">Сохранить</button>
                            </form>
                        ) : (
                            <>
                                <p className="card-text">
                                    <strong>Последнее использование: </strong>
                                    {song.last_event ? (
                                        <Link to={`/events/${song.last_event.id}`}>{song.last_event.name}</Link>
                                    ) : (
                                        "Не использовалась"
                                    )}
                                </p>
                                <p className="card-text">
                                    <strong>Загружено файлов:</strong> {fileStats.count} общим объемом {totalSizeMB} МБ
                                </p>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h2 className="h4 mb-0">Аранжировки</h2>
                    {canUpdate && isOwnSong && <button className="btn btn-primary btn-sm" onClick={handleShowAddArrangementModal} title="Добавить аранжировку"><i className="fas fa-plus"></i></button>}
                </div>
                <div className="card-body px-0">
                    {song.arrangements && song.arrangements.length > 0 ? (
                        <Accordion flush activeKey={activeArrangementKey} onSelect={setActiveArrangementKey}>
                            {song.arrangements.slice().sort((a, b) => {
                                if (a.is_default && !b.is_default) return -1;
                                if (!a.is_default && b.is_default) return 1;
                                return a.name.localeCompare(b.name);
                            }).map(arr => {
                                const mp3Files = arr.files.filter(f => f.file_type && f.file_type.toUpperCase() === 'MP3');
                                const documentFiles = arr.files.filter(f => f.file_type && ['PDF', 'DOC', 'DOCX', 'ONSONG'].includes(f.file_type.toUpperCase()));
                                const chordproFiles = arr.files.filter(f => f.file_type && f.file_type.toUpperCase() === 'CHORDPRO');
                                return (
                                    <Accordion.Item key={arr.id} eventKey={arr.id.toString()}>
                                        <Accordion.Header>
                                            <div className="w-100 d-flex justify-content-between align-items-center pe-2">
                                                <div>
                                                    <strong>{arr.name}</strong> {arr.is_default && <span className="badge bg-success ms-2"><i className="fas fa-star"></i></span>}
                                                    <div className="text-muted small">
                                                        <span>Тональность: {arr.key || '-'}</span>
                                                        <span className="mx-2">|</span>
                                                        <span>Темп: {arr.bpm || '-'} BPM</span>
                                                        <span className="mx-2">|</span>
                                                        <span>Размер: {arr.time_signature || '-'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            {canUpdate && isOwnSong && (
                                                <div className="d-flex justify-content-end mb-3">
                                                    <Dropdown>
                                                        <Dropdown.Toggle as={CustomToggle} id={`dropdown-arrangement-actions-${arr.id}`}>
                                                            <i className="fas fa-ellipsis-v"></i>
                                                        </Dropdown.Toggle>
                                                
                                                        <Dropdown.Menu>
                                                            <Dropdown.Item onClick={() => handleShowEditArrangementModal(arr)}>Редактировать</Dropdown.Item>
                                                            <Dropdown.Item onClick={() => handleShowUploadFileModal(arr)}>Загрузить файлы</Dropdown.Item>
                                                            <Dropdown.Item onClick={() => handleSetDefaultArrangement(arr.id)} disabled={arr.is_default}>Сделать по умолчанию</Dropdown.Item>
                                                            <Dropdown.Divider />
                                                            <Dropdown.Item onClick={() => handleDeleteArrangement(arr.id)} className="text-danger" disabled={song.arrangements.length <= 1}>Удалить</Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </div>
                                            )}
                                            <>
                                                <div className="mb-4">
                                                    <h6>MP3-файлы</h6>
                                                    {mp3Files.length > 0 ? (
                                                        <div>
                                                            {mp3Files.map(f => (
                                                                <div key={f.id} className="mb-3">
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <span className="text-truncate me-2">{f.original_filename} {formatSizeMB(f.file_size)}</span>
                                                                        <div className="d-flex align-items-center">
                                                                            <button onClick={() => handleDownloadFile(f)} className="btn btn-sm btn-link text-secondary me-1" title="Скачать">
                                                                                <i className="fas fa-download"></i>
                                                                            </button>
                                                                            {canUpdate && isOwnSong && (
                                                                                <button className="btn btn-sm btn-link text-danger" onClick={() => handleDeleteFile(f.id)} title="Удалить">
                                                                                    <i className="fas fa-trash"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <SecureAudioPlayer filePath={f.file_path} isPlaying={arr.id.toString() === activeArrangementKey} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : <p className="text-muted small">Нет MP3-файлов.</p>}
                                                </div>
                                                <div>
                                                    <h6>Документы</h6>
                                                    {documentFiles.length > 0 ? (
                                                        <div>
                                                            {documentFiles.map(f => {
                                                                const isPdf = f.file_type.toUpperCase() === 'PDF';
                                                                return (
                                                                    <div key={f.id} className="d-flex justify-content-between align-items-center mb-2">
                                                                        {isPdf ? (
                                                                            <button onClick={() => handleViewExistingPdf(f)} className="btn btn-link text-start p-0 text-truncate me-2" title="Просмотр" style={{ textTransform: 'none' }}>
                                                                                <i className="fas fa-file-pdf me-2"></i>
                                                                                {f.original_filename} {formatSizeMB(f.file_size)}
                                                                            </button>
                                                                        ) : (
                                                                            <span className="text-truncate me-2" style={{ textTransform: 'none' }}>
                                                                                <i className="fas fa-file-word me-2"></i>
                                                                                {f.original_filename} {formatSizeMB(f.file_size)}
                                                                            </span>
                                                                        )}
                                                                        <div className="d-flex align-items-center">
                                                                            <button onClick={() => handleDownloadFile(f)} className="btn btn-sm btn-link text-secondary me-1" title="Скачать">
                                                                                <i className="fas fa-download"></i>
                                                                            </button>
                                                                            {canUpdate && isOwnSong && (
                                                                                <button className="btn btn-sm btn-link text-danger" onClick={() => handleDeleteFile(f.id)} title="Удалить">
                                                                                    <i className="fas fa-trash"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : <p className="text-muted small">Нет документов.</p>}
                                                </div>
                                                <div className="mt-4">
                                                    <h6>Аккорды (ChordPro)</h6>
                                                    {chordproFiles.length > 0 ? (
                                                        <div>
                                                            {chordproFiles.map(f => (
                                                                <div key={f.id} className="d-flex justify-content-between align-items-center mb-2">
                                                                    <button onClick={() => handleShowRenderOptionsModal(f)} className="btn btn-link text-start p-0 text-truncate me-2" title="Просмотр" style={{ textTransform: 'none' }}>
                                                                        <i className="fas fa-file-alt me-2"></i>
                                                                        {f.original_filename} {formatSizeMB(f.file_size)}
                                                                    </button>
                                                                    <div className="d-flex align-items-center">
                                                                        <button onClick={() => handleDownloadFile(f)} className="btn btn-sm btn-link text-secondary me-1" title="Скачать">
                                                                            <i className="fas fa-download"></i>
                                                                        </button>
                                                                        {canUpdate && isOwnSong && (
                                                                            <button className="btn btn-sm btn-link text-danger" onClick={() => handleDeleteFile(f.id)} title="Удалить">
                                                                                <i className="fas fa-trash"></i>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : <p className="text-muted small">Нет файлов ChordPro.</p>}
                                                </div>
                                            </>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                );
                            })}
                        </Accordion>
                    ) : (
                        <p>Аранжировок пока нет.</p>
                    )}
                </div>
            </div>

            {/* Modals */}
            <ArrangementModal
                show={showArrangementModal}
                onHide={handleHideArrangementModal}
                arrangement={currentArrangement}
                onSave={handleSaveArrangement}
                isNew={isNewArrangement}
            />
            <UploadFileModal
                show={showUploadFileModal}
                onHide={handleHideUploadFileModal}
                arrangement={arrangementForUpload}
                onFilesUpdate={fetchSong}
            />
            <ChordProRenderOptionsModal
                show={showRenderOptionsModal}
                onHide={() => setShowRenderOptionsModal(false)}
                onConfirm={handleRenderPdf}
            />
            <ConfirmationModal
                show={showLimitInfoModal}
                title="Загрузка файлов недоступна"
                message="Загрузка файлов для вашей организации недоступна. Чтобы активировать эту возможность, свяжитесь с администратором EventPlan."
                onCancel={() => setShowLimitInfoModal(false)}
                confirmText="OK"
            />
            <ConfirmationModal
                show={showConfirmDeleteArrangementModal}
                title="Подтвердите удаление аранжировки"
                message="Вы уверены, что хотите удалить эту аранжировку?"
                onConfirm={handleConfirmDeleteArrangement}
                onCancel={() => setShowConfirmDeleteArrangementModal(false)}
                confirmText="Удалить"
                cancelText="Отмена"
            />
            <ConfirmationModal
                show={showConfirmDeleteFileModal}
                title="Подтвердите удаление файла"
                message="Вы уверены, что хотите удалить этот файл? Это действие необратимо."
                onConfirm={handleConfirmDeleteFile}
                onCancel={() => setShowConfirmDeleteFileModal(false)}
                confirmText="Удалить"
                cancelText="Отмена"
            />
            <ConfirmationModal
                show={showConfirmDeleteSongModal}
                title="Подтвердите удаление песни"
                message={`Вы уверены, что хотите удалить песню "${song?.title}"? Все связанные с ней файлы и пункты плана событий также будут удалены.`}
                onConfirm={handleConfirmDeleteSong}
                onCancel={() => setShowConfirmDeleteSongModal(false)}
                confirmText="Удалить"
                cancelText="Отмена"
            />
        </>
    );
};

export default SongDetailPage;